import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package2, Clock, CheckCircle2, Loader2, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle, XCircle, Download, Printer, Share2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/providers/AuthProvider";
import { toast } from "@/components/ui/use-toast";

interface Order {
  id: string;
  order_number: number;
  created_at: string;
  status: string;
  product_name: string;
  quantity: number;
  responsible_name: string;
  construction_name: string;
  department_name: string;
}

interface OrderStats {
  total: number;
  pending: number;
  completed: number;
}

const Orders = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats>({
    total: 0,
    pending: 0,
    completed: 0,
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/my-orders');
      return;
    }
    navigate('/orders/management');
    loadOrders();
    loadOrderStats();
  }, [isAdmin]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          products:product_id (name),
          departments:department_id (name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedOrders = data.map(order => ({
        id: order.id,
        order_number: order.order_number || parseInt(order.id),
        created_at: new Date(order.created_at).toLocaleDateString('pt-BR'),
        status: order.status,
        product_name: order.products?.name || 'N/A',
        quantity: order.quantity,
        responsible_name: order.responsible_name,
        construction_name: order.construction_name,
        department_name: order.departments?.name || 'N/A'
      }));

      setOrders(formattedOrders);
    } catch (error: any) {
      console.error('Erro ao carregar pedidos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os pedidos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadOrderStats = async () => {
    try {
      const { data: totalData, error: totalError } = await supabase
        .from('orders')
        .select('id', { count: 'exact' });

      const { data: pendingData, error: pendingError } = await supabase
        .from('orders')
        .select('id', { count: 'exact' })
        .eq('status', 'pending');

      const { data: completedData, error: completedError } = await supabase
        .from('orders')
        .select('id', { count: 'exact' })
        .eq('status', 'approved');

      if (totalError || pendingError || completedError) throw new Error('Erro ao carregar estatísticas');

      setStats({
        total: totalData?.length || 0,
        pending: pendingData?.length || 0,
        completed: completedData?.length || 0,
      });
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleApprove = async (orderId: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'approved' })
        .eq('id', orderId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Pedido aprovado com sucesso",
      });

      loadOrders();
      loadOrderStats();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Erro ao aprovar pedido",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (orderId: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'rejected' })
        .eq('id', orderId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Pedido rejeitado com sucesso",
      });

      loadOrders();
      loadOrderStats();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Erro ao rejeitar pedido",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (orderId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este pedido?')) return;

    try {
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Pedido excluído com sucesso",
      });

      loadOrders();
      loadOrderStats();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Erro ao excluir pedido",
        variant: "destructive",
      });
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gerenciamento de Pedidos</h1>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Pedidos</CardTitle>
            <Package2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pedidos Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pedidos Aprovados</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
          </CardContent>
        </Card>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nº do Pedido</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Responsável</TableHead>
              <TableHead>Produto</TableHead>
              <TableHead>Local</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  Nenhum pedido encontrado
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>#{order.order_number}</TableCell>
                  <TableCell>{order.created_at}</TableCell>
                  <TableCell>{order.responsible_name}</TableCell>
                  <TableCell>
                    {order.product_name} ({order.quantity} unidades)
                  </TableCell>
                  <TableCell>
                    {order.construction_name} - {order.department_name}
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        order.status === "approved"
                          ? "text-green-600"
                          : order.status === "rejected"
                          ? "text-red-600"
                          : "text-yellow-600"
                      }
                    >
                      {order.status === "pending"
                        ? "Pendente"
                        : order.status === "approved"
                        ? "Aprovado"
                        : "Rejeitado"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {order.status === "pending" && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleApprove(order.id)}
                            className="text-green-600"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(order.id)}
                            className="text-red-600"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(order.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Orders;